import React from "react";
import { Select } from "antd";
import { LANGUAGES } from "../../utils/languages";

/**
 * LanguageSelect Component
 *
 * A reusable language selector component based on Ant Design Select.
 * Supports all Ant Design Select features including search, single/multi select, etc.
 *
 * @param {string} value - Selected language value(s)
 * @param {function} onChange - Callback function when selection changes
 * @param {string} placeholder - Placeholder text (default: "Select Language")
 * @param {boolean} allowClear - Whether to show clear button (default: true)
 * @param {string} className - Additional CSS classes
 * @param {string} mode - Selection mode: undefined (single) or "multiple" for multi-select
 * @param {boolean} showSearch - Enable search functionality (default: true)
 * @param {string|number} maxTagCount - Max tag count to show in multi-select mode (default: "responsive")
 * @param {boolean} disabled - Whether the select is disabled
 * @param {object} style - Inline styles
 * @param {array} languages - Custom language list (default: LANGUAGES)
 * @param {string} labelKey - Key to use for option label (default: "label")
 * @param {string} valueKey - Key to use for option value (default: "value")
 */
const LanguageSelect = ({
  value,
  onChange,
  placeholder = "Select Language",
  allowClear = true,
  className = "",
  mode,
  showSearch = true,
  maxTagCount = "responsive",
  disabled = false,
  style,
  languages = LANGUAGES,
  labelKey = "label",
  valueKey = "value",
  ...rest
}) => {
  // Filter function for search
  const filterOption = (input, option) => {
    const label = option?.children?.toLowerCase() || "";
    return label.includes(input.toLowerCase());
  };

  return (
    <Select
      showSearch={showSearch}
      className={`w-full ${className}`}
      allowClear={allowClear}
      value={value}
      onChange={onChange}
      mode={mode}
      maxTagCount={maxTagCount}
      placeholder={placeholder}
      optionFilterProp="children"
      filterOption={filterOption}
      disabled={disabled}
      style={style}
      {...rest}
    >
      {languages.map(lang => (
        <Select.Option key={lang[valueKey]} value={lang[valueKey]}>
          {lang[labelKey]}
        </Select.Option>
      ))}
    </Select>
  );
};

export default LanguageSelect;
